// src/features/treasury/components/TreasuryDashboard/TransactionsTable.jsx
import React, { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useReactToPrint } from 'react-to-print';
import Table from '../../../../shared/components/ui/Table';
import { formatNumber, formatDateTime, formatDate } from '../../../../shared/utils/formatters';
import api from '../../../../api/axiosInstance';
import styles from './TreasuryDashboard.module.css';

const TRANSACTION_TYPES = [
  { value: 'all',             label: 'كل الحركات'       },
  { value: 'deposit',         label: 'إيداع نقدي'       },
  { value: 'withdrawal',      label: 'سحب نقدي'         },
  { value: 'payment_in',      label: 'دفعة حجز (وارد)'  },
  { value: 'expense_out',     label: 'مصروف حجز'        },
  { value: 'general_expense', label: 'مصروف عام'        },
  { value: 'printing_cost',   label: 'تكلفة طباعة'      },
];

const PrintableTreasury = React.forwardRef(({ transactions, statistics, filters, treasury }, ref) => {
  const today = formatDate(new Date());
  return (
    <div ref={ref} style={{ direction: 'rtl', fontFamily: 'Arial, sans-serif', padding: '24px', color: '#111', background: '#fff' }}>
      <div style={{ textAlign: 'center', marginBottom: '24px', borderBottom: '2px solid #111', paddingBottom: '16px' }}>
        <h1 style={{ fontSize: '20px', margin: '0 0 4px', fontWeight: 'bold' }}>تقرير حركات الخزينة</h1>
        <p style={{ fontSize: '12px', color: '#555', margin: 0 }}>تاريخ الطباعة: {today}</p>
        {(filters.start_date || filters.end_date) && (
          <p style={{ fontSize: '12px', color: '#555', margin: '4px 0 0' }}>الفترة: {filters.start_date || '—'} إلى {filters.end_date || '—'}</p>
        )}
      </div>
      {treasury && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' }}>
          {[
            { label: 'الرصيد الحالي', value: treasury.current_balance, color: '#0066cc' },
            { label: 'إجمالي الوارد', value: statistics?.total_inflow,  color: '#1a7a4a' },
            { label: 'إجمالي الصادر', value: statistics?.total_outflow, color: '#cc2200' },
          ].map(s => (
            <div key={s.label} style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '11px', color: '#666', marginBottom: '4px' }}>{s.label}</div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: s.color, direction: 'ltr' }}>{formatNumber(s.value || 0, 0)} د.ع</div>
            </div>
          ))}
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
        <thead>
          <tr style={{ background: '#f0f0f0' }}>
            {['#', 'نوع العملية', 'المبلغ', 'الوصف / البيان', 'الرصيد بعد', 'التاريخ', 'المسؤول'].map(h => (
              <th key={h} style={{ border: '1px solid #ccc', padding: '6px 8px', textAlign: 'right', fontWeight: 'bold' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {transactions.map((row, i) => (
            <tr key={row.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
              <td style={{ border: '1px solid #ddd', padding: '5px 8px', textAlign: 'center' }}>{i + 1}</td>
              <td style={{ border: '1px solid #ddd', padding: '5px 8px' }}>
                <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: 'bold', background: row.is_inflow ? '#e6f4ec' : '#fce8e6', color: row.is_inflow ? '#1a7a4a' : '#cc2200' }}>{row.transaction_type_display}</span>
              </td>
              <td style={{ border: '1px solid #ddd', padding: '5px 8px', direction: 'ltr', textAlign: 'right', fontWeight: 'bold', color: row.is_inflow ? '#1a7a4a' : '#cc2200' }}>{row.is_inflow ? '+' : '-'} {formatNumber(row.amount, 0)}</td>
              <td style={{ border: '1px solid #ddd', padding: '5px 8px', maxWidth: '200px' }}>{row.description}</td>
              <td style={{ border: '1px solid #ddd', padding: '5px 8px', direction: 'ltr', textAlign: 'right' }}>{formatNumber(row.balance_after, 0)}</td>
              <td style={{ border: '1px solid #ddd', padding: '5px 8px', whiteSpace: 'nowrap' }}>{formatDateTime(row.transaction_date)}</td>
              <td style={{ border: '1px solid #ddd', padding: '5px 8px' }}>{row.created_by_name}</td>
            </tr>
          ))}
          {transactions.length === 0 && (
            <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: '#888', border: '1px solid #ddd' }}>لا توجد حركات</td></tr>
          )}
        </tbody>
      </table>
      {statistics && (
        <div style={{ marginTop: '20px', borderTop: '2px solid #111', paddingTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '32px', fontSize: '13px' }}>
          <span>إجمالي الوارد: <strong style={{ color: '#1a7a4a', direction: 'ltr', display: 'inline-block' }}>{formatNumber(statistics.total_inflow, 0)} د.ع</strong></span>
          <span>إجمالي الصادر: <strong style={{ color: '#cc2200', direction: 'ltr', display: 'inline-block' }}>{formatNumber(statistics.total_outflow, 0)} د.ع</strong></span>
          <span>الصافي: <strong style={{ color: statistics.net_change >= 0 ? '#1a7a4a' : '#cc2200', direction: 'ltr', display: 'inline-block' }}>{formatNumber(statistics.net_change, 0)} د.ع</strong></span>
        </div>
      )}
      <div style={{ marginTop: '40px', fontSize: '10px', color: '#999', textAlign: 'center' }}>تم إنشاء هذا التقرير بواسطة نظام إدارة الاستوديو — {today}</div>
    </div>
  );
});
PrintableTreasury.displayName = 'PrintableTreasury';

const TransactionsTable = ({
  transactions, statistics, loading, error, filters, onFilterChange, treasury, onRefresh,
  currentPage, totalPages, totalCount, pageSize, onPageChange, loadAllTransactionsForPrint,
}) => {
  const printRef = useRef(null);
  const [deleting, setDeleting] = useState(null);
  const [printData, setPrintData] = useState(null);
  const [preparingPrint, setPreparingPrint] = useState(false);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `treasury-report-${formatDate(new Date())}`,
    onBeforePrint: async () => {
      // الجدول معروض صفحة بصفحة، لكن الطباعة يجب أن تشمل كل الحركات المطابقة للفلاتر
      setPreparingPrint(true);
      try {
        const all = await loadAllTransactionsForPrint();
        flushSync(() => setPrintData(all));
      } finally {
        setPreparingPrint(false);
      }
    },
  });

  const handleDelete = async (row) => {
    if (!['deposit', 'withdrawal'].includes(row.transaction_type)) {
      alert('لا يمكن حذف هذه الحركة — يمكن حذف الإيداع والسحب اليدوي فقط.');
      return;
    }
    const typeLabel = row.is_inflow ? 'إيداع' : 'سحب';
    if (!window.confirm(`حذف ${typeLabel} بمبلغ ${formatNumber(row.amount, 0)} د.ع؟\nسيتم عكس المبلغ في رصيد الخزينة.`)) return;
    setDeleting(row.id);
    try {
      await api.delete(`/treasury-transactions/${row.id}/`);
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.error || 'فشل الحذف.');
    } finally {
      setDeleting(null);
    }
  };

  const columns = [
    { key: 'index', label: '#', render: (_, __, i) => i + 1, width: '50px' },
    {
      key: 'transaction_type_display', label: 'نوع العملية',
      render: (val, row) => (
        <span className={`${styles.badge} ${row.is_inflow ? styles.badgeSuccess : styles.badgeDanger}`}>{val}</span>
      ),
    },
    {
      key: 'amount', label: 'المبلغ',
      render: (val, row) => (
        <span className={row.is_inflow ? styles.textSuccess : styles.textDanger}
          style={{ fontWeight: 'bold', direction: 'ltr', display: 'inline-block' }}>
          {row.is_inflow ? '+' : '-'} {formatNumber(val, 0)}
        </span>
      ),
    },
    { key: 'description', label: 'الوصف / البيان' },
    {
      key: 'balance_after', label: 'الرصيد بعد',
      render: val => <span style={{ direction: 'ltr', display: 'inline-block' }}>{formatNumber(val, 0)}</span>,
    },
    { key: 'transaction_date', label: 'التاريخ والوقت', render: val => formatDateTime(val) },
    { key: 'created_by_name', label: 'المسؤول' },
    // ✅ زر الحذف — للإيداع والسحب اليدوي فقط
    {
      key: 'actions', label: '', width: '70px',
      render: (_, row) => {
        const canDelete = ['deposit', 'withdrawal'].includes(row.transaction_type);
        if (!canDelete) return <span style={{ color: 'var(--text-muted)', fontSize: '10px' }}>—</span>;
        return (
          <button type="button" disabled={deleting === row.id} onClick={() => handleDelete(row)}
            style={{
              padding: '3px 10px', fontSize: '11px', borderRadius: 'var(--radius-sm)',
              border: '0.5px solid var(--color-danger-border)',
              background: 'var(--color-danger-subtle)', color: 'var(--color-danger-text)',
              cursor: 'pointer', fontFamily: 'var(--font-family)',
              opacity: deleting === row.id ? 0.6 : 1, whiteSpace: 'nowrap',
            }}>
            {deleting === row.id ? '...' : 'حذف'}
          </button>
        );
      },
    },
  ];

  return (
    <div>
      <div className={styles.filterBar}>
        <input type="text" name="search" value={filters.search} onChange={onFilterChange}
          placeholder="بحث في الوصف والملاحظات..." className={styles.filterInput} />
        <select name="transaction_type" value={filters.transaction_type} onChange={onFilterChange} className={styles.filterInput}>
          {TRANSACTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <input type="date" name="start_date" value={filters.start_date} onChange={onFilterChange} className={styles.filterInput} title="من تاريخ" />
        <input type="date" name="end_date" value={filters.end_date} onChange={onFilterChange} className={styles.filterInput} title="إلى تاريخ" />
        <button type="button" onClick={handlePrint} disabled={preparingPrint}
          style={{ padding: '6px 16px', fontSize: '12px', fontWeight: 500, borderRadius: 'var(--radius-md)', cursor: preparingPrint ? 'wait' : 'pointer', fontFamily: 'var(--font-family)', border: '0.5px solid var(--color-info-border)', background: 'var(--color-info-subtle)', color: 'var(--color-info-text)', whiteSpace: 'nowrap', flexShrink: 0, opacity: preparingPrint ? 0.6 : 1 }}>
          {preparingPrint ? 'تجهيز الطباعة...' : 'طباعة PDF'}
        </button>
      </div>

      {error && <p className={styles.errorMsg}>{error}</p>}

      <div className={styles.tableContainer}>
        <Table columns={columns} data={transactions} loading={loading} emptyMessage="لا توجد حركات تطابق الفلاتر المحددة." />
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageChange={onPageChange}
        loading={loading}
      />

      {statistics && (
        <div className={styles.statsSummary}>
          <div><strong>إجمالي الوارد:</strong> <span className={styles.textSuccess}>{formatNumber(statistics.total_inflow, 0)} د.ع</span></div>
          <div><strong>إجمالي الصادر:</strong> <span className={styles.textDanger}>{formatNumber(statistics.total_outflow, 0)} د.ع</span></div>
          <div><strong>الصافي:</strong> <span style={{ fontWeight: 'bold', color: statistics.net_change >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>{formatNumber(statistics.net_change, 0)} د.ع</span></div>
        </div>
      )}

      <div style={{ display: 'none' }}>
        <PrintableTreasury ref={printRef} transactions={printData ?? transactions} statistics={statistics} filters={filters} treasury={treasury} />
      </div>
    </div>
  );
};

// ── مكوّن Pagination ─────────────────────────────────────────
const Pagination = ({ currentPage, totalPages, totalCount, pageSize, onPageChange, loading }) => {
  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  const getPageNumbers = () => {
    const delta = 2;
    const pages = [];
    const left = Math.max(2, currentPage - delta);
    const right = Math.min(totalPages - 1, currentPage + delta);

    pages.push(1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  return (
    <div className={styles.pagination}>
      <span className={styles.paginationInfo}>
        {startItem}–{endItem} من {totalCount} حركة
      </span>
      <div className={styles.paginationControls}>
        <button
          className={styles.pageBtn}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
        >
          ›
        </button>
        {getPageNumbers().map((page, i) =>
          page === '...' ? (
            <span key={`d${i}`} className={styles.pageDots}>…</span>
          ) : (
            <button
              key={page}
              className={`${styles.pageBtn} ${page === currentPage ? styles.pageBtnActive : ''}`}
              onClick={() => onPageChange(page)}
              disabled={loading}
            >
              {page}
            </button>
          )
        )}
        <button
          className={styles.pageBtn}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages || loading}
        >
          ‹
        </button>
      </div>
    </div>
  );
};

export default TransactionsTable;